import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import type { CalendarEvent } from "@/features/calendar-events/types/calendarEventTypes";
import { eventTouchesDay } from "@/features/calendar-events/utils/agenda";
import { useServices } from "@/features/services/hooks/serviceQueries";
import { CalendarDayButton } from "@/shared/components/shadcn/Calendar";
import { DateCalendar } from "@/shared/components/ui/DateCalendar";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { getBusinessDateKey } from "@/shared/utils/formatters";
import { calendarDate, calendarDateKey } from "@/shared/utils/calendarDate";
import type { Appointment } from "../types/appointmentTypes";
import "./appointments.css";

interface AppointmentsCalendarProps {
  appointments?: Appointment[];
  events?: CalendarEvent[];
  month?: Date;
  onMonthChange?: (month: Date) => void;
  selectedDay?: Date;
  onDateSelect?: (date: Date) => void;
  disablePast?: boolean;
}

export function AppointmentsCalendar({ appointments = [], events = [], month, onMonthChange, selectedDay, onDateSelect, disablePast = false }: AppointmentsCalendarProps) {
  const { business } = useCurrentBusiness();
  const services = useServices();
  const timeZone = business?.timezone ?? "UTC";
  const today = calendarDate(getBusinessDateKey(new Date(), timeZone));
  const appointmentsByDay = new Map<string, Appointment[]>();
  for (const appointment of appointments) {
    if (!appointment.startAt || !["pending", "confirmed"].includes(appointment.status)) continue;
    const key = getBusinessDateKey(appointment.startAt, timeZone);
    const dayAppointments = appointmentsByDay.get(key) ?? [];
    dayAppointments.push(appointment);
    appointmentsByDay.set(key, dayAppointments);
  }
  return (
    <>
      {services.error && <ErrorState message={services.error.message} onRetry={() => void services.refetch()} />}
      <DateCalendar
        month={month}
        onMonthChange={onMonthChange}
        onDateSelect={onDateSelect}
        selectedDay={selectedDay}
        today={today}
        disabled={disablePast ? (date) => calendarDateKey(date) < calendarDateKey(today) : undefined}
        className="appointment-calendar"
        dayButton={({ children, modifiers, day, ...props }) => {
          const dayAppointments = modifiers.outside ? [] : appointmentsByDay.get(calendarDateKey(day.date)) ?? [];
          const dayEvents = events.filter((event) => eventTouchesDay(event, calendarDateKey(day.date), timeZone));
          return (
            <CalendarDayButton
              day={day}
              modifiers={modifiers}
              {...props}
              aria-label={(dayAppointments.length > 0
                ? `${props["aria-label"]}, ${dayAppointments.length} ${dayAppointments.length === 1 ? "appointment" : "appointments"}`
                : props["aria-label"]) + (dayEvents.length > 0 ? `, ${dayEvents.length} ${dayEvents.length === 1 ? "calendar event" : "calendar events"}` : "")}
            >
              <span className="relative flex min-h-11 w-full items-center justify-center">
                {children}
                {(dayAppointments.length > 0 || dayEvents.length > 0) && (
                  <span aria-hidden="true" className="absolute bottom-1 flex gap-0.5">
                    {dayAppointments.slice(0, dayEvents.length > 0 ? 4 : 5).map((appointment) => {
                      const firstService = [...appointment.services].sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0))[0];
                      const color = services.data?.find((service) => service.id === firstService?.serviceId)?.color;
                      return (
                        <span
                          key={appointment.id}
                          className={`size-1 rounded-full ${modifiers.selected ? "bg-primary-foreground" : "bg-primary"}`}
                          style={{ backgroundColor: modifiers.selected ? undefined : color || undefined }}
                        />
                      );
                    })}
                    {dayEvents.slice(0, Math.max(1, 5 - dayAppointments.length)).map((event) => (
                      <span key={`event-${event.id}`} className={`size-1 rounded-sm ${modifiers.selected ? "bg-primary-foreground" : "bg-primary"} ${!event.blocksAvailability ? "opacity-50" : ""}`} />
                    ))}
                  </span>
                )}
              </span>
            </CalendarDayButton>
          );
        }}
      />
    </>
  );
}
